import { Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import {
  GbpersonService,
  PersonGbSearchErweitertRequest,
  PersonGbSearchErweitertRequestMeta,
  PersonGbSearchResponseMeta,
  PersonGbSearchStandardRequest,
  PersonStammMasterListEntryMeta
} from '../../../core/gb-api';
import { ListLoader } from '../../../shared/components/list-loader/list-loader';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import { ListLoaderPagination, ListLoaderPaginationBuilder } from '../../../shared/components/list-loader/list-loader-pagination';
import { catchError, map, switchMap } from 'rxjs/operators';
import { PaginationLookupResult } from '../../../shared/components/list-loader/pagination-lookup-result';
import { ListLoaderPreloaded } from '../../../shared/components/list-loader/list-loader-preloaded';
import { TranslateService } from '@ngx-translate/core';
import { CapAlertService } from '../../../core/extension/exceptions/cap-alert.service';
import { LinkComponent, NavTarget } from '../../../shared/components/link/link.component';
import { CapExceptionHandler } from '../../../core/extension/exceptions/cap-exception-handler';
import { ResolverValidationExceptionHandler } from '../../../core/extension/exceptions/exception-handler-rule';
import { RouteHistoryService } from '../../../core/services/route-history.service';
import { CapAlertLevel } from '../../../core/extension/exceptions/cap-alert-level.enum';
import { AbstractComponent } from '../../../shared/components/abstract-component/abstract.component';
import { FormatConfigService } from '../../../core/services/format-config.service';

@Injectable()
export class PersonstammListResolver extends AbstractComponent {
  private readonly PAGE_SIZE = 15;
  private readonly CURRENT_PAGE = 1;
  listLoader: ListLoader<PersonStammMasterListEntryMeta>;
  private listLoaderSubject = new BehaviorSubject<ListLoader<PersonStammMasterListEntryMeta>>(undefined);
  listLoader$ = this.listLoaderSubject.asObservable();

  private standardSearchRequest: PersonGbSearchStandardRequest;

  constructor(
    private personService: GbpersonService,
    private capAlertService: CapAlertService,
    private translate: TranslateService,
    private exceptionHandler: CapExceptionHandler,
    private resolverValidationExceptionHandler: ResolverValidationExceptionHandler,
    private router: Router,
    private route: ActivatedRoute,
    private routeHistory: RouteHistoryService,
    private formatConfig: FormatConfigService
  ) {
    super();
  }

  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<ListLoader<PersonStammMasterListEntryMeta>>
    | Promise<ListLoader<PersonStammMasterListEntryMeta>>
    | ListLoader<PersonStammMasterListEntryMeta> {
    if (route.params?.searchMode === 'personGbStandard') {
      return this.searchPersonGbStandard(route);
    }

    const searchRequest = <PersonGbSearchErweitertRequest>(JSON.parse(route.queryParams.request) || {});

    if (!searchRequest.rolle) {
      this.router.navigate([this.routeHistory.previousRoute]);
      return EMPTY;
    }

    if (searchRequest.rolle === 'ALLE') {
      return this.searchRolleALLE(searchRequest);
    } else {
      return this.searchPersonGbErweitert(searchRequest);
    }
  }

  private searchPersonGbErweitert(searchRequest: PersonGbSearchErweitertRequest): Observable<ListLoader<PersonStammMasterListEntryMeta>> {
    const observable = this.personService.searchPersonGbErweitert(searchRequest).pipe(
      switchMap((response) => {
        return this.handleDataLoaded(
          new ListLoaderPreloaded(15, 1, response.results, response.recordsFound),
          response,
          searchRequest.rolle
        );
      }),
      catchError((err) => {
        let alertText = '';
        if (err.status === 403) {
          for (const entry of err.error.errorMessage.split(',')) {
            const relevantPartOfMessage = entry.trim().split(':')[1].trim().split(' ')[0].split('.').slice(-1)[0];
            const inputField = this.translate.instant(relevantPartOfMessage);
            alertText = alertText + this.translate.instant('KeineBerechtigungFuer', { inputField: inputField });
            if (relevantPartOfMessage === 'rolle') {
              alertText = alertText + ' ' + this.translate.instant(searchRequest.rolle);
            }
          }
          this.capAlertService.warning(alertText, CapAlertLevel.CONTENT_HEADER, true);
          this.router.navigate(['/person/']);
        } else {
          this.handleError(err);
        }
        return EMPTY;
      })
    );
    return observable;
  }

  private searchRolleALLE(searchRequest: PersonGbSearchErweitertRequest): Observable<ListLoader<PersonStammMasterListEntryMeta>> {
    let observable = this.personService.searchPersonGbErweitert(searchRequest).pipe(
      map((response) => {
        if (response === undefined || response.recordsFound === 0) {
          this.capAlertService.info(this.translate.instant('KeineResultateGefunden'), undefined, true);
          this.router.navigate([this.routeHistory.previousRoute]);
        }
        return <PaginationLookupResult<PersonStammMasterListEntryMeta>>{
          result: response.results,
          totalItems: response.recordsFound
        };
      }),
      catchError((err) => {
        // this.handleError() doesn't display messages due to conflict between navigate() and 'delayed', hence manually:
        if (err.status === 400 && err.error && err.error.results) {
          const res = err.error.results;
          const msgKey = res.length > 0 ? res[0].messageKey : 'searchZuWenigSuchkriterien';
          this.capAlertService.warning(this.translate.instant(msgKey), CapAlertLevel.CONTENT_HEADER, true);
          this.router.navigate([this.routeHistory.previousRoute]);
          return EMPTY;
        } else {
          throw err;
        }
      })
    );

    const listLoader: ListLoaderPagination<PersonStammMasterListEntryMeta> =
      ListLoaderPaginationBuilder.builder<PersonStammMasterListEntryMeta>()
        .setNoSortModel(true)
        .setCurrentPage(1)
        .setPageSize(15)
        .setThrowError(false)
        .setLookupItemFunc((offset, limit, sortColumn) => {
          searchRequest.elasticSearchOptions.offset = offset;
          searchRequest.elasticSearchOptions.limit = limit;
          searchRequest.personStammSort = sortColumn?.sortEnumBackend;
          searchRequest.sortDirection = sortColumn?.sortDirectionBackend;
          return observable;
        })
        .build();

    this.setListLoader(listLoader);
    return of(this.listLoader);
  }

  private searchPersonGbStandard(route: ActivatedRouteSnapshot): Observable<ListLoader<PersonStammMasterListEntryMeta>> {
    let request = JSON.parse(route.queryParams.request);
    this.standardSearchRequest = <PersonGbSearchStandardRequest>(request || {});

    this.addSub(
      this.personService.searchPersonGbStandardHits(this.standardSearchRequest).subscribe((data) => {
        const observable = (offset, limit, sortColumn) => this.createObservable(offset, limit);
        const listLoader: ListLoaderPagination<PersonStammMasterListEntryMeta> =
          ListLoaderPaginationBuilder.builder<PersonStammMasterListEntryMeta>()
            .setNoSortModel(true)
            .setLookupItemFunc(observable)
            .setCurrentPage(this.CURRENT_PAGE)
            .setPageSize(this.PAGE_SIZE)
            .setTotalItems(data)
            .build();
        this.setListLoader(listLoader);
      })
    );

    return of(this.listLoader);
  }

  private handleDataLoaded(
    listLoader: ListLoader<PersonStammMasterListEntryMeta>,
    response: PersonGbSearchResponseMeta,
    rolle: PersonGbSearchErweitertRequestMeta.RolleEnum
  ): Observable<ListLoader<PersonStammMasterListEntryMeta>> {
    const result = response.results;

    // nothing found
    if (result && result.length === 0) {
      this.capAlertService.info(this.translate.instant('KeineResultateGefunden'));
      this.router.navigate([this.routeHistory.previousRoute]);
      return EMPTY;
      // command arrived
    } else if (result && result.length === 1) {
      const commands: any[] = [this.getNavTargetPath()[rolle], result[0].masterId.id];
      if (result[0].version.id) {
        commands.push(result[0].version.id.id);
      }
      this.router.navigate(commands);
      return EMPTY;
      // result list available
    } else {
      this.setListLoader(listLoader);
      return of(this.listLoader);
    }
  }

  private handleError(error) {
    this.router.navigate([this.routeHistory.previousRoute]);
    if (this.resolverValidationExceptionHandler.errorHandled(error)) {
      return EMPTY;
    }
    this.exceptionHandler.handleError(error);
    return EMPTY;
  }

  private setListLoader(loader: ListLoader<PersonStammMasterListEntryMeta>) {
    this.listLoader = loader;
    this.addLoader(this.listLoader);
    this.listLoaderSubject.next(this.listLoader);
  }

  private getNavTargetPath() {
    return {
      EIGENTUEMER: LinkComponent.getPath(NavTarget.NAV_PERSON_EIGENTUEMER_VERHAELTNISSE),
      UEBRIGE_BERECHTIGTE: LinkComponent.getPath(NavTarget.NAV_PERSON_UEBRIGEBERECHTIGTE),
      GLAEUBIGER_SCHULDNER: LinkComponent.getPath(NavTarget.NAV_PERSON_GLAEUBIGERAUSZUG),
      ALLE: LinkComponent.getPath(NavTarget.NAV_PERSON_STAMM)
    };
  }

  private createObservable(offset: number, limit: number): Observable<any> {
    this.standardSearchRequest.offset = offset;
    this.standardSearchRequest.limit = limit;
    return this.personService.searchPersonGbStandard(this.standardSearchRequest).pipe(
      map((data) => {
        return <PaginationLookupResult<PersonStammMasterListEntryMeta>>{
          result: data.results,
          totalItems: data.recordsFound
        };
      })
    );
  }
}
